import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { createNewAttempt, getCourseById, getEnrollmentByUserAndCourse } from '@nsc/simple-lms/api/lms-queries.js';
import type { LMSCourse } from '@nsc/simple-lms/entities/LMSCourse.js';
import { type LMSCourseEnrollment, LMSEnrollmentStatus } from '@nsc/simple-lms/entities/LMSCourseEnrollment.js';
import { type LMSCoursePage, LMSCoursePageType } from '@nsc/simple-lms/entities/LMSCoursePage.js';
import { clampScorePercent } from '@nsc/simple-lms/client/score-utils.js';
import { html } from 'lit';
import * as m from '../../localization/out/messages.js';
import { router } from '../startup.js';

const StatCard = ({
  icon,
  label,
  value,
  tone,
}: {
  icon: string;
  label: string;
  value: string | number;
  tone?: string;
}) => {
  const toneClass = tone ? `${tone}/10 text-${tone}` : 'text-primary';

  return html`
    <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm px-4 py-3 flex items-center gap-3">
      <span class="${icon} text-xl ${toneClass}"></span>
      <div class="flex flex-col leading-tight">
        <span class="text-xs text-base-content/60">${label}</span>
        <span class="text-lg font-semibold text-base-content">${value}</span>
      </div>
    </div>
  `;
};

const CourseStats = ({ course }: { course: Pick<LMSCourse, 'passingScorePercent' | 'pages'> }) => {
  const videoCount = course.pages?.filter(p => p.type === LMSCoursePageType.VIDEO).length ?? 0;
  const questionCount = course.pages?.filter(p => p.type === LMSCoursePageType.QUESTION).length ?? 0;

  return html`
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
      ${StatCard({
        icon: 'icon-[material-symbols--play-circle-outline]',
        label: m.course_stats_videos(),
        value: videoCount,
      })}
      ${StatCard({
        icon: 'icon-[material-symbols--quiz-outline]',
        label: m.course_stats_questions(),
        value: questionCount,
        tone: 'secondary',
      })}
      ${StatCard({
        icon: 'icon-[material-symbols--percent]',
        label: m.course_stats_passing(),
        value: `${course.passingScorePercent}%`,
        tone: 'accent',
      })}
    </div>
  `;
};

const EnrollmentStatus = ({
  enrollment,
  course,
}: {
  enrollment:
    | Pick<LMSCourseEnrollment, '_id' | 'status' | 'scorePercent' | 'attempts' | 'lockedAt' | 'lockReason'>
    | null
    | undefined;
  course: Pick<LMSCourse, '_id' | 'passingScorePercent' | 'pages'>;
}) => {
  const [creatingAttempt, setCreatingAttempt] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  const isPassed = enrollment?.status === LMSEnrollmentStatus.PASSED;
  const isFailed = enrollment?.status === LMSEnrollmentStatus.FAILED;
  const inProgress = enrollment?.status === LMSEnrollmentStatus.IN_PROGRESS;
  const isAssigned = enrollment?.status === LMSEnrollmentStatus.ASSIGNED;
  const isLocked = !!enrollment?.lockedAt;
  const canStart = !!enrollment && (course.pages?.length ?? 0) > 0;

  if (!enrollment) {
    return html`<p class="text-sm text-base-content/50 mb-6">${m.course_not_assigned()}</p>`;
  }

  const statusTone = isLocked
    ? 'error'
    : isPassed
      ? 'success'
      : isFailed
        ? 'error'
        : inProgress
          ? 'warning'
          : isAssigned
            ? 'info'
            : 'info';
  const statusIcon = isPassed
    ? 'icon-[material-symbols--check-circle-outline]'
    : isLocked
      ? 'icon-[material-symbols--lock-outline]'
      : isFailed
        ? 'icon-[material-symbols--error-outline]'
        : inProgress
          ? 'icon-[material-symbols--schedule-outline]'
          : 'icon-[material-symbols--assignment-outline]';
  const statusTitle = isPassed
    ? m.course_status_passed_title()
    : isLocked
      ? m.course_status_locked_title()
      : isFailed
        ? m.course_status_failed_title()
        : inProgress
          ? m.course_status_running()
          : m.academy_status_assigned();
  const allScores = (enrollment.attempts ?? []).map(a => clampScorePercent(a.scorePercent));
  const bestScore = Math.max(...allScores);
  const scoresText = allScores.map(a => `${a}%`).join(', ');

  const statusSub = isPassed
    ? m.course_status_passed_attempts_sub({ bestScore, scores: scoresText || '0%' })
    : isLocked
      ? m.course_status_locked_sub({ reason: enrollment?.lockReason ?? '' })
      : isFailed
        ? m.course_status_failed_attempts_sub({
            bestScore,
            required: course.passingScorePercent ?? 0,
            scores: scoresText || '0%',
          })
        : inProgress
          ? m.course_status_progress_saved()
          : '';

  const handleRetry = async () => {
    // ?disabled lands a tick after the click, so re-entrancy has to be checked here too.
    if (!enrollment?._id || creatingAttempt) return;
    setCreatingAttempt(true);
    setRetryError(null);
    try {
      await createNewAttempt(enrollment._id)({ _id: 1, status: 1, attempts: { completedAt: 1, scorePercent: 1 } });
      router.goTo('coursePlayer', {
        params: { courseId: course._id, enrollmentId: enrollment._id },
      });
    } catch (error) {
      setRetryError((error as Error)?.message ?? m.course_retry_failed());
    } finally {
      setCreatingAttempt(false);
    }
  };

  const handleStartOrContinue = async () => {
    if (!enrollment?._id || creatingAttempt) return;
    setRetryError(null);

    if (!isAssigned) {
      router.goTo('coursePlayer', {
        params: { courseId: course._id, enrollmentId: enrollment._id },
      });
      return;
    }

    setCreatingAttempt(true);
    try {
      await createNewAttempt(enrollment._id)({ _id: 1, status: 1, attempts: { completedAt: 1, scorePercent: 1 } });
      router.goTo('coursePlayer', {
        params: { courseId: course._id, enrollmentId: enrollment._id },
      });
    } catch (error) {
      setRetryError((error as Error)?.message ?? m.course_start_failed());
    } finally {
      setCreatingAttempt(false);
    }
  };

  const actionArea = canStart
    ? html`
        ${isLocked
          ? html`<button class="btn btn-disabled w-full rounded-xl" disabled>
              ${m.course_max_attempts_reached()}
            </button>`
          : isPassed || isFailed
            ? html`
                <button class="btn btn-primary w-full rounded-xl" ?disabled=${creatingAttempt} @click=${handleRetry}>
                  ${creatingAttempt ? html`<span class="loading loading-spinner loading-sm"></span>` : m.course_retry()}
                </button>
              `
            : html`
                <button
                  class="btn btn-primary w-full rounded-xl"
                  ?disabled=${creatingAttempt}
                  @click=${handleStartOrContinue}
                >
                  ${creatingAttempt
                    ? html`<span class="loading loading-spinner loading-sm"></span>`
                    : inProgress
                      ? m.course_continue()
                      : m.course_start()}
                </button>
              `}
      `
    : null;

  return html`
    <div class="rounded-2xl border border-${statusTone} bg-${statusTone}/10 px-4 py-4 flex gap-3 items-start mb-5">
      <span class="${statusIcon} text-xl text-${statusTone}"></span>
      <div class="flex-1">
        <p class="font-semibold text-${statusTone}">${statusTitle}</p>
        <p class="text-sm text-base-content/70">${statusSub}</p>
      </div>
      ${isFailed
        ? html`<span class="badge badge-outline badge-${statusTone} text-xs"
            >${m.course_badge_attempt_finished()}</span
          >`
        : ''}
    </div>
    <div class="flex flex-col gap-3 mb-6">${actionArea}</div>
    ${retryError ? html`<p class="text-sm text-error mb-4">${retryError}</p>` : ''}
  `;
};

const CourseContent = ({ pages }: { pages: Pick<LMSCoursePage, '_id' | 'type' | 'title' | 'order'>[] }) => html`
  <h3 class="font-semibold text-base-content mb-3">${m.course_content_section_title()}</h3>
  <div class="flex flex-col gap-2">
    ${pages.map(
      (page, i) => html`
        <div
          class="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-base-100 border border-base-200 shadow-sm hover:shadow transition-shadow"
        >
          <div
            class="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${page.type ===
            LMSCoursePageType.VIDEO
              ? 'bg-primary/10 text-primary'
              : 'bg-secondary/10 text-secondary'}"
          >
            ${i + 1}
          </div>
          <span
            class="${page.type === LMSCoursePageType.VIDEO
              ? 'icon-[material-symbols--play-circle-outline] text-primary'
              : 'icon-[material-symbols--quiz-outline] text-secondary'} text-lg shrink-0"
          ></span>
          <span class="text-sm flex-1">
            ${page.title ??
            (page.type === LMSCoursePageType.VIDEO
              ? m.course_content_title_default_video()
              : m.course_content_title_default_question())}
          </span>
          <span class="icon-[material-symbols--chevron-right] text-base-content/30 text-lg shrink-0"></span>
        </div>
      `,
    )}
  </div>
`;

export const CourseDetailPage = stateful(({ courseId }: { courseId: string }) => {
  const user = useObservable(() => currentUser(), []);

  const {
    value: course,
    loading: courseLoading,
    error: courseError,
  } = useQuery(
    () =>
      getCourseById(courseId)({
        _id: 1,
        title: 1,
        description: 1,
        language: 1,
        passingScorePercent: 1,
        pages: { _id: 1, courseId: 1, order: 1, type: 1, title: 1 },
      }),
    [courseId],
  );

  const {
    value: enrollment,
    loading: enrollmentLoading,
    error: enrollmentError,
  } = useQuery(() => {
    if (!user?._id) return Promise.resolve(null);
    return getEnrollmentByUserAndCourse(
      user._id,
      courseId,
    )({
      _id: 1,
      courseId: 1,
      userId: 1,
      status: 1,
      lockedAt: 1,
      lockReason: 1,
      scorePercent: 1,
      completedAt: 1,
      attempts: { passed: 1, completedAt: 1, scorePercent: 1 },
    });
  }, [courseId, user?._id]);

  const loading = enrollmentLoading || courseLoading;
  const error = courseError ?? enrollmentError;

  if (loading) {
    return html`
      <div class="flex items-center justify-center h-full">
        <span class="loading loading-spinner loading-lg text-primary"></span>
      </div>
    `;
  }

  if (error) {
    const messages: Record<string, string> = {
      courseError: m.course_load_error(),
      enrollmentError: m.enrollment_load_error(),
    };
    const key = courseError ? 'courseError' : enrollmentError ? 'enrollmentError' : 'courseError';
    return html`
      <div class="flex items-center justify-center h-full">
        <p class="text-error">${messages[key]}: ${error.message}</p>
      </div>
    `;
  }

  if (!course) {
    return html`
      <div class="flex items-center justify-center h-full">
        <p class="text-base-content/60">${m.player_not_found()}</p>
      </div>
    `;
  }

  return html`
    <div class="px-4 py-5 max-w-3xl mx-auto">
      <div class="flex items-center gap-2 mb-4">
        <a href="${router.link('academy')}" class="btn btn-ghost btn-sm gap-1">
          <span class="icon-[material-symbols--arrow-back] text-lg"></span>
          ${m.course_back()}
        </a>
      </div>

      <div class="">
        <div class="flex items-start justify-between gap-3 mb-3">
          <div class="flex-1 min-w-0">
            <h2 class="text-3xl font-bold leading-tight mb-1">${course.title}</h2>
            ${course.language ? html`<span class="badge badge-outline badge-sm">${course.language}</span>` : ''}
          </div>
        </div>
        ${course.description
          ? html`<p class="text-sm text-base-content/70 leading-relaxed mb-4">${course.description}</p>`
          : html`<p class="text-sm text-base-content/50 mb-4">${m.course_no_description()}</p>`}
        ${CourseStats({ course })} ${EnrollmentStatus({ enrollment, course })}
      </div>

      <div class="">
        ${course.pages?.length
          ? CourseContent({ pages: course.pages.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) })
          : html`<p class="text-sm text-base-content/60">${m.course_no_content()}</p>`}
      </div>
    </div>
  `;
});
